if (!customElements.get('warranty-toggle')) {
  customElements.define(
    'warranty-toggle',
    class WarrantyToggle extends HTMLElement {
      // localStorage flag so a customer who turns the warranty off is not
      // nagged with it being auto-added again on the next cart change.
      static OPT_OUT_KEY = 'warranty:opt-out';
      // Guards against firing several auto-adds while the first one is in flight.
      static autoAddInFlight = false;

      constructor() {
        super();

        this.warrantyId = this.getAttribute('data-warranty-id');
        this.warrantyKey = this.getAttribute('data-warranty-key');
        this.inCart = this.getAttribute('data-in-cart') === 'true';
        this.hasItems = this.getAttribute('data-has-items') === 'true';
        this.autoAdd = this.getAttribute('data-auto-add') === 'true';
      }

      get checkbox() {
        return this.querySelector('input[type="checkbox"]');
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get optedOut() {
        try {
          return localStorage.getItem(WarrantyToggle.OPT_OUT_KEY) === '1';
        } catch (e) {
          return false;
        }
      }

      set optedOut(value) {
        try {
          value
            ? localStorage.setItem(WarrantyToggle.OPT_OUT_KEY, '1')
            : localStorage.removeItem(WarrantyToggle.OPT_OUT_KEY);
        } catch (e) {
          // Private mode / storage disabled — silently ignore.
        }
      }

      connectedCallback() {
        if (this.checkbox === null) return;

        // When the warranty switch is turned on or off by the customer.
        this.checkbox.addEventListener('change', theme.utils.debounce((event) => {
          event.target.checked ? this.turnOn() : this.turnOff();
        }, 300));

        // Auto-add by default: there are products in the cart, the warranty isn't
        // there yet, and the customer hasn't deliberately turned it off.
        if (this.autoAdd && this.hasItems && !this.inCart && !this.optedOut && !WarrantyToggle.autoAddInFlight) {
          WarrantyToggle.autoAddInFlight = true;
          this.addWarranty();
        }
      }

      turnOn() {
        this.optedOut = false;
        this.addWarranty();
      }

      turnOff() {
        this.optedOut = true;
        this.removeWarranty();
      }

      // Collects the section ids that should be re-rendered with the cart response.
      bundledSections() {
        let sections = [this.sectionId];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        return [...new Set(sections)];
      }

      // Adds the warranty with /cart/add.js — the same non-destructive endpoint the
      // bundle selector uses, so existing line items and their discounts stay intact.
      addWarranty() {
        this.enableLoading();

        const body = JSON.stringify({
          items: [{ id: parseInt(this.warrantyId, 10), quantity: 1 }],
          sections: this.bundledSections()
        });

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              console.error('Warranty add failed:', parsedState.description || parsedState.message);
              this.revert(true);
              return;
            }

            // /cart/add.js returns the added line(s) + sections, not the full cart.
            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson.sections = parsedState.sections;
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'warranty-toggle', cart: cartJson });
          })
          .catch((error) => {
            console.error(error);
            this.revert(true);
          })
          .finally(() => {
            WarrantyToggle.autoAddInFlight = false;
            this.disableLoading();
          });
      }

      // Removes the warranty by its line key with /cart/change.js so no other line is touched.
      removeWarranty() {
        if (!this.warrantyKey) {
          this.disableLoading();
          return;
        }

        this.enableLoading();

        const body = JSON.stringify({
          id: this.warrantyKey,
          quantity: 0,
          sections: this.bundledSections()
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status || parsedState.errors) {
              console.error('Warranty remove failed:', parsedState.description || parsedState.message || parsedState.errors);
              this.revert(false);
              return;
            }
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'warranty-toggle', cart: parsedState });
          })
          .catch((error) => {
            console.error(error);
            this.revert(false);
          })
          .finally(() => {
            this.disableLoading();
          });
      }

      // Restores the switch to match the real cart state when an update fails.
      revert(wasAdd) {
        if (this.checkbox) this.checkbox.checked = !wasAdd;
      }

      enableLoading() {
        const loader = this.querySelector('.loader');
        if (loader) loader.hidden = false;
        if (this.checkbox) this.checkbox.disabled = true;
      }

      disableLoading() {
        const loader = this.querySelector('.loader');
        if (loader) loader.hidden = true;
        if (this.checkbox) this.checkbox.disabled = false;
      }
    }
  );
}
